// Libraries
import { useEffect, useReducer, useState, Reducer } from "react";
import { Box, Typography } from "@mui/material";
import { GridColDef, GridColumnHeaderParams } from "@mui/x-data-grid-premium";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

// Components
import DesiredVehicleTable from "./DesiredVehicleTable";
import SearchInventory from "./SearchInventory";
import SelectedVehicleTable from "./SelectedVehicleTable";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";

// Context
import { useLeadsContext } from "@/DataPages/context/crm/LeadsContext";

// Types
import { IDesiredVehicle } from "@/DataPages/context/crm/interfaces";
import { IPageParam, IPaginatedResponse } from "@/Common/api/types/pagination";
import { RootState } from "@/redux/store";
import { TDesiredVehicleSearchField, TSetAction } from "../../../types";

// Constants
import { CELL_CLASS_NAME } from "@/DataPages/components/Inventory/Tables/commonProps";
import { DISABLED_MULTIPLE_SELECTION, INITIAL_STATE_SEARCH_DESIRED_VEHICLE, MAX_DESIRED_VEHICLE } from "../../../constants";
import { CRM_KEYS } from "@/DataPages/components/CRM/constants";

// Helpers
import { determineDesiredVehicles, isNewVehicleAlreadySelected } from "../../../helpers";
import { or } from "@/Common/helpers/sonar";

// Hooks
import { usePaginatedResource } from "@/Common/hooks/Pagination";

// Services
import { getInventoryDesiredVehicles, IInventoryDesiredVehicles, TSearchVehicleInventory } from "@/DataPages/api/crm/queries";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";

const COLUMNS: GridColDef[] = [
	{ field: "_id", headerName: "ID", width: 80, filterable: false, resizable: false },
	{
		field: "type",
		headerName: "Status",
		width: 60,
		minWidth: 60,
		maxWidth: 60,
		filterable: true,
		resizable: false,
		renderHeader: renderHeaderHandler,
		cellClassName: () => CELL_CLASS_NAME,
	},
	{
		field: "stockNumber",
		headerName: "Stock #",
		width: 80,
		minWidth: 80,
		maxWidth: 80,
		filterable: true,
		resizable: false,
		renderHeader: renderHeaderHandler,
		cellClassName: () => CELL_CLASS_NAME,
	},
	{
		field: "vin",
		headerName: "VIN",
		width: 120,
		minWidth: 120,
		maxWidth: 120,
		filterable: true,
		resizable: false,
		renderHeader: renderHeaderHandler,
		cellClassName: () => CELL_CLASS_NAME,
	},
	{
		field: "year",
		headerName: "Year",
		width: 50,
		minWidth: 50,
		maxWidth: 50,
		filterable: true,
		resizable: false,
		renderHeader: renderHeaderHandler,
		cellClassName: () => CELL_CLASS_NAME,
	},
	{
		field: "make",
		headerName: "Make",
		width: 100,
		minWidth: 100,
		maxWidth: 100,
		filterable: true,
		resizable: false,
		renderHeader: renderHeaderHandler,
		cellClassName: () => CELL_CLASS_NAME,
	},
	{
		field: "model",
		headerName: "Model",
		width: 80,
		minWidth: 80,
		maxWidth: 80,
		filterable: true,
		resizable: false,
		renderHeader: renderHeaderHandler,
		cellClassName: () => CELL_CLASS_NAME,
	},
	{
		field: "style",
		headerName: "Style",
		width: 140,
		minWidth: 140,
		maxWidth: 140,
		filterable: true,
		resizable: false,
		renderHeader: renderHeaderHandler,
		cellClassName: () => CELL_CLASS_NAME,
	},
];

function renderHeaderHandler(params: GridColumnHeaderParams) {
	return <TypographyHeader fontSize="12px" fontLineHeight="12px" params={params} className="font-montserrat font-medium" />;
}

function reducer(state: TDesiredVehicleSearchField, action: TSetAction) {
	switch (action.type) {
		case "setType":
			return { ...state, type: action.payload };
		case "setStockNumber":
			return { ...state, stockNumber: action.payload };
		case "setVin":
			return { ...state, vin: action.payload };
		case "setMake":
			return { ...state, make: action.payload };
		case "setModel":
			return { ...state, model: action.payload };
		case "setStyle":
			return { ...state, style: action.payload };
		case "setYear":
			return { ...state, year: action.payload };
		default:
			return state;
	}
}

export default function DesiredVehicleSection() {
	const { locationId = "" } = useSelector((state: RootState) => state.location?.location) ?? {};

	const { isSavingLead, onChangeContext } = useLeadsContext();

	const [displayTable, setDisplayTable] = useState<boolean>(true);
	const [selectedVehicles, setSelectedVehicles] = useState<IDesiredVehicle[]>([]);
	const [selectedInventoryVins, setSelectedInventoryVins] = useState<string[]>([]);
	const [searchFilters, setSearchFilters] = useState<Partial<TSearchVehicleInventory>>({});
	const [isSearchDone, setIsSearchDone] = useState<boolean>(false);

	const [desiredVehicle, dispatchUpdateDesiredVehicle] = useReducer<Reducer<TDesiredVehicleSearchField, TSetAction>>(
		reducer,
		INITIAL_STATE_SEARCH_DESIRED_VEHICLE
	);

	const genericDispatcher = (actionType: TSetAction["type"]) => (value: string) =>
		dispatchUpdateDesiredVehicle({ type: actionType, payload: value });

	const { make, model, stockNumber, style, type, vin, year } = desiredVehicle;

	const {
		data: inventoryData,
		isLoading: isInventoryLoading,
		hasNextPage,
	} = usePaginatedResource<IInventoryDesiredVehicles, IPageParam<string>>({
		queryKey: [CRM_KEYS.QUERY.INVENTORY_DESIRED_VEHICLES, locationId, searchFilters],
		queryFn: async ({ pageParam }) => {
			const { first, after } = pageParam as IPageParam<string>;
			const result = await getInventoryDesiredVehicles(locationId, searchFilters, { first, after });

			if (!result) {
				return { edges: [], pageInfo: { after: "", endCursor: "", first: 0, hasNextPage: false } } as IPaginatedResponse<
					IInventoryDesiredVehicles,
					IPageParam<string>,
					string
				>;
			}

			return result;
		},
		getNextPageParam: buildGetNextPageParam(100),
		enabled: Boolean(locationId) && Object.keys(searchFilters).length > 0,
		initialPageParam: { after: "", first: 100 },
		staleTime: Infinity,
		gcTime: 5000,
		fetchAllPages: true,
	});

	function onSearch(param: IDesiredVehicle) {
		const { type, stockNumber, vin, make, model, style, year } = param;

		const newSearchFilters: Partial<TSearchVehicleInventory> = {
			type,
			stockNumber,
			vin,
			make,
			model,
			style,
			year,
		};

		setSearchFilters(newSearchFilters);
		setIsSearchDone(true);
	}

	function handleDesiredVehicles(newDesiredVehicle: string[]) {
		const vehiclesFromInventory = inventoryData.filter((vehicle) => newDesiredVehicle.includes(vehicle._id));

		const searchableVehicles = inventoryData.map((vehicle) => vehicle._id);
		const currentVehicles = selectedVehicles.filter((vehicle) => !searchableVehicles.includes(vehicle?._id ?? ""));
		const desiredVehicles = determineDesiredVehicles(currentVehicles, vehiclesFromInventory);

		setSelectedVehicles(desiredVehicles);
		setSelectedInventoryVins(newDesiredVehicle);
	}

	function removeDesiredVehicles(_id: string) {
		const newDesiredVehicles = selectedVehicles.filter((vehicle) => vehicle._id !== _id);
		const newDesiredVehicleVins = newDesiredVehicles
			.map((vehicle) => vehicle._id)
			.filter((vehicle): vehicle is string => typeof vehicle === "string");

		setSelectedVehicles(newDesiredVehicles);
		setSelectedInventoryVins(newDesiredVehicleVins);
	}

	function onAddAnyway() {
		const newDesired: IDesiredVehicle = {
			type: type,
			vin: vin,
			year: year,
			make: make,
			model: model,
			style: style,
			stockNumber: stockNumber,
			_id: uuidv4(),
		};

		const isMaxLimitReached = or(
			!DISABLED_MULTIPLE_SELECTION && selectedVehicles.length >= MAX_DESIRED_VEHICLE,
			DISABLED_MULTIPLE_SELECTION && selectedVehicles.length >= 1
		);

		if (isMaxLimitReached || isNewVehicleAlreadySelected(newDesired, selectedVehicles) || Boolean(newDesired.vin)) {
			return;
		}

		setSelectedVehicles((prev) => [...prev, newDesired]);
	}

	useEffect(() => {
		onChangeContext("vehicle", "desiredVehicle", selectedVehicles);
	}, [selectedVehicles]);

	return (
		<Box>
			<Typography className="!font-montserrat !font-[500] !text-[16px] !text-gray-text !mb-3">Desired Vehicle</Typography>
			<SearchInventory
				states={{
					typeState: [type, genericDispatcher("setType")],
					vinState: [vin, genericDispatcher("setVin")],
					yearState: [year, genericDispatcher("setYear")],
					makeState: [make, genericDispatcher("setMake")],
					modelState: [model, genericDispatcher("setModel")],
					styleState: [style, genericDispatcher("setStyle")],
					stockNumberState: [stockNumber, genericDispatcher("setStockNumber")],
				}}
				disabled={isSavingLead}
				isLoadingSearch={isInventoryLoading}
				displayTable={displayTable}
				isSearchDone={isSearchDone}
				hasNextPage={hasNextPage}
				setDisplayTable={setDisplayTable}
				onSearch={onSearch}
			/>
			<DesiredVehicleTable
				disabled={isSavingLead}
				isSearchDone={isSearchDone}
				selectedRows={selectedInventoryVins}
				displayTable={displayTable}
				isLoading={isInventoryLoading}
				data={inventoryData}
				columns={COLUMNS}
				hasNextPage={hasNextPage}
				amountSelectedVehicles={selectedVehicles?.length || 0}
				onSelectedRows={(rows) => handleDesiredVehicles(rows)}
				onAddAnyway={onAddAnyway}
			/>
			<SelectedVehicleTable
				columns={COLUMNS}
				data={selectedVehicles}
				disabled={isSavingLead}
				onRemove={(_id) => removeDesiredVehicles(_id)}
			/>
		</Box>
	);
}
